import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faSpinner,
  faRotateLeft,
  faClock,
  faCheck,
  faBrain,
  faBan,
  faCalendarDay,
  faPlay,
  faClone,
  faFire,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import flashcardService from '../../services/flashcardService';
import './Flashcards.css';

// Spec §7.2: format seconds as "7m" under an hour, otherwise "1.2h".
const formatTime = (seconds) => {
  const s = seconds || 0;
  const minutes = s / 60;
  if (minutes < 60) return `${Math.round(minutes)}m`;
  return `${(minutes / 60).toFixed(1)}h`;
};

const Tile = ({ iconCls, icon, num, label }) => (
  <div className="fc-tile">
    <span className={`fc-tile-icon ${iconCls}`}>
      <FontAwesomeIcon icon={icon} />
    </span>
    <div className="fc-tile-body">
      <span className="fc-tile-num">{num}</span>
      <span className="fc-tile-lbl">{label}</span>
    </div>
  </div>
);

const StatsModal = ({ deck, onClose }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    flashcardService
      .getDeckStats(deck.id)
      .then((data) => {
        if (active) setStats(data);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [deck.id]);

  const breakdown = stats?.responseBreakdown || {};
  const cardsStudied = stats?.cardsStudied || {};
  const time = stats?.studyTimeSeconds || {};
  const cardCount = stats?.cardCount || 0;
  const learned = stats?.learnedCount || 0;
  const progress = cardCount > 0 ? Math.round((learned / cardCount) * 100) : 0;

  return (
    <div className="fc-modal-overlay" onClick={onClose}>
      <div
        className="fc-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="fc-modal-header">
          <h2>{deck.name} — My Stats</h2>
          <button type="button" className="fc-modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="fc-modal-body">
          {loading && (
            <div className="fc-state">
              <FontAwesomeIcon icon={faSpinner} className="fc-spin fc-state-icon" />
              <p>Loading your stats…</p>
            </div>
          )}

          {error && !loading && (
            <div className="fc-state">
              <div className="fc-state-icon">
                <FontAwesomeIcon icon={faTriangleExclamation} />
              </div>
              <h3>Couldn't load stats</h3>
              <p>Please close and try again.</p>
            </div>
          )}

          {!loading && !error && stats && (
            <>
              <div className="fc-stats-section">
                <h3>Response Breakdown</h3>
                <div className="fc-stat-tiles fc-tiles-5">
                  <Tile iconCls="fc-ico-again" icon={faRotateLeft} num={breakdown.again || 0} label="Repeat" />
                  <Tile iconCls="fc-ico-hard" icon={faClock} num={breakdown.hard || 0} label="Hard" />
                  <Tile iconCls="fc-ico-good" icon={faCheck} num={breakdown.good || 0} label="Fair" />
                  <Tile iconCls="fc-ico-easy" icon={faBrain} num={breakdown.easy || 0} label="Easy" />
                  <Tile iconCls="fc-ico-never" icon={faBan} num={breakdown.never || 0} label="Never" />
                </div>
              </div>

              <div className="fc-stats-section">
                <h3>Study Activity</h3>
                <div className="fc-stat-tiles fc-tiles-3">
                  <Tile iconCls="fc-ico-accent" icon={faCalendarDay} num={cardsStudied.today || 0} label="Cards Today" />
                  <Tile iconCls="fc-ico-accent" icon={faCalendarDay} num={cardsStudied.week || 0} label="This Week" />
                  <Tile iconCls="fc-ico-accent" icon={faCalendarDay} num={cardsStudied.month || 0} label="This Month" />
                </div>
              </div>

              <div className="fc-stats-section">
                <h3>Study Time</h3>
                <div className="fc-stat-tiles fc-tiles-3">
                  <Tile iconCls="fc-ico-accent" icon={faClock} num={formatTime(time.today)} label="Today" />
                  <Tile iconCls="fc-ico-accent" icon={faClock} num={formatTime(time.week)} label="This Week" />
                  <Tile iconCls="fc-ico-accent" icon={faClock} num={formatTime(time.month)} label="This Month" />
                </div>
              </div>

              <div className="fc-stats-section">
                <h3>Overview</h3>
                <div className="fc-stat-tiles fc-tiles-4">
                  <Tile iconCls="fc-ico-accent" icon={faPlay} num={stats.totalSessions || 0} label="Sessions" />
                  <Tile iconCls="fc-ico-accent" icon={faClone} num={stats.totalCardsStudied || 0} label="Cards Studied" />
                  <Tile iconCls="fc-ico-good" icon={faCheck} num={`${Math.round(stats.averageScorePercent || 0)}%`} label="Avg Score" />
                  <Tile iconCls="fc-ico-hard" icon={faFire} num={stats.dayStreak || 0} label="Day Streak" />
                </div>
              </div>

              <div className="fc-stats-overall">
                <h3>Overall Progress</h3>
                <div className="fc-progress-wrap">
                  <div className="fc-progress-track">
                    <div className="fc-progress-fill" style={{ width: `${progress}%` }} />
                  </div>
                  <span className="fc-progress-label">
                    {progress}% complete · {learned}/{cardCount} learned
                    {stats.suspendedCount ? ` · ${stats.suspendedCount} suspended` : ''}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatsModal;
